/** @module Scheduled activity dialog: opens immediately, loads owner-scoped schedules, and uses existing confirmation cards for Resume/Delete. */
import { useEffect, useState } from 'react';
import { Clock, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  fluxAgentTriggersApi,
  type ScheduledTrigger,
  type TriggerRun,
} from '@/lib/fluxAgentTriggersApi';
import type { FluxAgentAction } from '@/lib/fluxAgentApi';
import { FluxAgentActionCards } from './FluxAgentActionCards';

/** Dialog visibility and navigation callbacks. */
export interface FluxAgentScheduledProps {
  /** Whether the list is visible. */
  open: boolean;
  /** Close or reopen the dialog. */
  onOpenChange: (open: boolean) => void;
  /** Open the conversation containing a run's reply. */
  onConversation: (id: string) => void;
}

/** One schedule and its asynchronously loaded history. */
function ScheduledRow({
  trigger,
  onChanged,
  onConversation,
}: {
  /** Server-shaped schedule. */
  trigger: ScheduledTrigger;
  /** Refresh the list after a confirmed change. */
  onChanged: () => void;
  /** Navigate to the results conversation. */
  onConversation: (id: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<FluxAgentAction | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<TriggerRun[] | null>(null);
  const change = async (action: 'pause' | 'resume' | 'delete') => {
    setBusy(true);
    setError(null);
    if (action === 'pause') {
      const result = await fluxAgentTriggersApi.pause(trigger.id);
      if (result.ok) onChanged();
      else setError(result.message);
    } else {
      const result = await fluxAgentTriggersApi.stage(trigger.id, action);
      if (result.ok) setPending(result.data.action);
      else setError(result.message);
    }
    setBusy(false);
  };
  const showHistory = async () => {
    setHistoryOpen(current => !current);
    if (historyOpen || history) return;
    const result = await fluxAgentTriggersApi.runs(trigger.id);
    if (result.ok) setHistory(result.data.runs);
    else setError(result.message);
  };
  return (
    <li className="space-y-2 rounded-lg border p-3" aria-label={trigger.title}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="min-w-0 break-words font-semibold">{trigger.title}</h3>
        <span className="text-muted-foreground text-xs capitalize">
          {trigger.status}
          {trigger.running ? ' · Running' : ''}
        </span>
      </div>
      <p className="break-words text-sm">{trigger.instruction}</p>
      <p className="text-muted-foreground text-xs">
        {trigger.scheduleText} · {trigger.timezone}
      </p>
      {trigger.nextRuns.length > 0 && <p className="text-sm">Next: {trigger.nextRuns[0].label}</p>}
      {trigger.lastError && (
        <p className="text-destructive text-sm" role="status">
          {trigger.lastError}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {trigger.status === 'active' && (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => void change('pause')}>
            Pause
          </Button>
        )}
        {trigger.status === 'paused' && (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => void change('resume')}>
            Resume…
          </Button>
        )}
        <Button size="sm" variant="outline" disabled={busy} onClick={() => void change('delete')}>
          Delete…
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => void showHistory()}
          aria-expanded={historyOpen}
        >
          Run history
        </Button>
        {trigger.conversationId && (
          <Button size="sm" variant="ghost" onClick={() => onConversation(trigger.conversationId!)}>
            Open conversation
          </Button>
        )}
      </div>
      {busy && (
        <p className="text-muted-foreground text-xs" role="status">
          Updating…
        </p>
      )}
      {error && (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      )}
      {pending && (
        <FluxAgentActionCards
          actions={[pending]}
          onResolved={action => {
            setPending(action);
            onChanged();
          }}
        />
      )}
      {historyOpen && (
        <div className="space-y-2 border-t pt-2" aria-label="Run history">
          {!history ? (
            error ? null : (
              <p role="status">Loading history…</p>
            )
          ) : history.length === 0 ? (
            <p className="text-muted-foreground text-sm">No runs yet.</p>
          ) : (
            history.map(run => (
              <div key={run.id} className="text-sm">
                <p className="text-muted-foreground text-xs">
                  <time dateTime={run.scheduledFor}>
                    {new Date(run.scheduledFor).toLocaleString(undefined, {
                      timeZone: trigger.timezone,
                    })}
                  </time>{' '}
                  · {run.status}
                </p>
                <p>{run.error || run.summary || 'Run in progress.'}</p>
                {run.pendingCards > 0 && (
                  <p>
                    {run.pendingCards} action{run.pendingCards === 1 ? '' : 's'} waiting for
                    confirmation.
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </li>
  );
}

/** Open immediately with loading feedback; no data fetch blocks the dialog becoming visible. */
export function FluxAgentScheduled({
  open,
  onOpenChange,
  onConversation,
}: FluxAgentScheduledProps) {
  const [rows, setRows] = useState<ScheduledTrigger[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fluxAgentTriggersApi.list().then(result => {
      if (cancelled) return;
      if (result.ok) setRows(result.data.triggers);
      else setError(result.message);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open, revision]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85dvh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Scheduled
          </DialogTitle>
          <DialogDescription>
            Ask Flux Agent to schedule an instruction. Review and confirm its schedule before it
            runs. Pause cannot recall actions already started. Runs with your current tool
            permissions. Actions that require confirmation wait for you. Remote MCP tools set to
            Allow in Settings can run while you’re away.
          </DialogDescription>
        </DialogHeader>
        <Button
          variant="outline"
          size="sm"
          className="w-fit"
          onClick={() => setRevision(value => value + 1)}
          disabled={loading}
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
        {loading && (
          <p role="status" className="flex items-center gap-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading schedules…
          </p>
        )}
        {error && (
          <p role="alert" className="text-destructive text-sm">
            {error}
          </p>
        )}
        {!loading && !error && rows.length === 0 && (
          <p className="text-muted-foreground text-sm">
            Nothing scheduled yet. Try “Every weekday at 8, help me plan my day.”
          </p>
        )}
        <ul className="space-y-3" aria-label="Scheduled triggers">
          {rows.map(trigger => (
            <ScheduledRow
              key={trigger.id}
              trigger={trigger}
              onChanged={() => setRevision(value => value + 1)}
              onConversation={id => {
                onOpenChange(false);
                onConversation(id);
              }}
            />
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
